'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot } from '@radix-ui/react-slot';
import clsx from 'clsx';
import * as React from 'react';
import { cn } from '../../utils';
import { Label } from '../Label/Label.js';
const FormItemContext = React.createContext({});
/**
 * Wraps a control and allows use of an a11y-enabled label, description, and error message.
 *
 * https://ui.shadcn.com/docs/components/form
 */
const FormItemRoot = React.forwardRef(({ className, error, ...props }, ref) => {
    const id = React.useId();
    return (_jsx(FormItemContext.Provider, { value: { id, error }, children: _jsx("div", { ref: ref, className: cn('space-y-2', className), ...props }) }));
});
FormItemRoot.displayName = 'FormItem';
export function useFormField() {
    const itemContext = React.useContext(FormItemContext);
    const { id, error } = itemContext;
    if (!id) {
        throw new Error(`useFormField must be used in a FormItemContext`);
    }
    return {
        id,
        error,
        formItemId: `${id}-form-item`,
        formDescriptionId: `${id}-form-item-description`,
        formMessageId: `${id}-form-item-message`,
    };
}
const FormLabel = React.forwardRef(({ className, isOptional, renderOnPageBackground, ...props }, ref) => {
    const { error, formItemId } = useFormField();
    return (_jsxs(Label, { ref: ref, className: cn(error && 'text-destructive', 'flex items-center justify-between', renderOnPageBackground
            ? 'text-brand-text-page'
            : 'text-brand-text-content', className), htmlFor: formItemId, ...props, children: [props.children, isOptional && (_jsx("span", { className: clsx('ml-2 text-sm opacity-70', renderOnPageBackground
                    ? 'text-brand-text-page'
                    : 'text-brand-text-content'), children: "Optional" }))] }));
});
FormLabel.displayName = 'FormLabel';
const FormControl = React.forwardRef(({ ...props }, ref) => {
    const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
    return (_jsx(Slot, { ref: ref, id: formItemId, "aria-describedby": !error
            ? `${formDescriptionId}`
            : `${formDescriptionId} ${formMessageId}`, "aria-invalid": !!error, ...props }));
});
FormControl.displayName = 'FormControl';
const FormDescription = React.forwardRef(({ className, renderOnPageBackground, ...props }, ref) => {
    const { formDescriptionId } = useFormField();
    return (_jsx("p", { ref: ref, id: formDescriptionId, className: cn('text-sm', renderOnPageBackground
            ? 'text-brand-text-page'
            : 'text-brand-text-content', className), ...props }));
});
FormDescription.displayName = 'FormDescription';
const FormError = React.forwardRef(({ className, children, ...props }, ref) => {
    const { error, formMessageId } = useFormField();
    const errorText = error instanceof Error ? error.message : error;
    const body = errorText ? errorText : children;
    if (!body) {
        return null;
    }
    return (_jsx("p", { ref: ref, id: formMessageId, className: cn('text-sm font-medium text-destructive', className), ...props, children: body }));
});
FormError.displayName = 'FormError';
export const FormItem = Object.assign(FormItemRoot, {
    Label: FormLabel,
    Control: FormControl,
    Description: FormDescription,
    Error: FormError,
});
//# sourceMappingURL=FormItem.js.map